import { SuspensePageBoundary } from "@-/dev.improve.haniverse.components.ui.suspense-page";
import { FrostedBackdropTarget } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { BrowsePrimitivesListSkeleton } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._components.loading-skeletons";
import { BrowsePrimitivesCompoundsSkeleton } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.primitives._components.compounds-skeleton";
import { PrimaryControlsStripTitle } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import { LazyBrowseTabContent } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._lib.focus";
import { BrowsePrimitivesPrimaryControlsStripExpansionContent } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.primitives._components.primary-controls-strip-expansion-content";
import { BrowsePrimitivesPageToolbar } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.primitives._components.toolbar";
import { PrimaryControlsStripBack } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import { Slot, usePathname } from "@-/com.npmjs.expo-router";
import { View } from "@-/com.npmjs.react-native";

/**
 * Shared chrome for every Primitives sub-route (List / Compounds): the nav header, the dataset
 * progress expansion, and a suspense boundary around the active sub-route rendered by a plain
 * `Slot`. The view-switch pills live in the floating `BrowsePrimitivesPageToolbar`, rendered as an
 * overlay sibling of the `Slot` (mirroring the Levels layout) — the active pill is derived from the
 * pathname and each pill `replace`s to its sibling route via a page navigator. The filter control
 * lives in the per-page `BrowsePrimitivesPageToolbarActions`, and each body view reads the shared
 * primitives list with `useBrowsePrimitivesSuspense()` (cached across sub-route switches by the
 * query key).
 *
 * We render a plain `Slot` rather than a nested `MaterialTopTabs` pager: a `MaterialTopTabs` nested
 * inside the parent browse pager fails to lay out its `tabBar`, leaving the switch pills invisible
 * and un-tappable (the same failure that pushed the Levels layout off `MaterialTopTabs`).
 */
export function BrowsePrimitivesLayout() {
  const isCompounds = usePathname().endsWith("/compounds");
  const loadingBody = isCompounds ? (
    <BrowsePrimitivesCompoundsSkeleton />
  ) : (
    <BrowsePrimitivesListSkeleton />
  );
  return (
    <>
      <PrimaryControlsStripBack hidden />
      <LazyBrowseTabContent>
        <BrowsePrimitivesPrimaryControlsStripExpansionContent />
      </LazyBrowseTabContent>
      <PrimaryControlsStripTitle>Primitives</PrimaryControlsStripTitle>
      <View className="min-h-0 flex-1 bg-background">
        <FrostedBackdropTarget className="min-h-0 flex-1">
          <SuspensePageBoundary fallback={loadingBody} location={import.meta.location}>
            <LazyBrowseTabContent fallback={loadingBody}>
              <Slot />
            </LazyBrowseTabContent>
          </SuspensePageBoundary>
        </FrostedBackdropTarget>
        <BrowsePrimitivesPageToolbar />
      </View>
    </>
  );
}
